import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';

// Icons from Remix Icons (react-icons/ri)
import { 
    RiBriefcase4Fill, 
    RiSunFill, 
    RiMoonClearFill, 
    RiTeamLine, 
    RiBuilding4Line, 
    RiUserFollowLine, 
    RiTv2Line, 
    RiLogoutBoxRLine, 
    RiKey2Line, 
    RiUserAddLine, 
    RiFileTextLine, 
    RiMagicLine, 
    RiShieldUserLine, 
    RiMenu4Line, 
    RiCloseLine 
} from 'react-icons/ri';

export default function Navbar() {
    const { user, userType, logout } = useAuth();
    const { theme, toggleTheme } = useTheme();
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();

    const handleLogout = async () => {
        setMobileMenuOpen(false);
        await logout();
        navigate('/login');
    };

    const roleBadges = {
        admin: { label: 'ADMIN', color: 'bg-purple-500/20 text-purple-400 border-purple-500/30' },
        coordinator: { label: 'COORDINATOR', color: 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30' },
        company_hr: { label: 'COMPANY HR', color: 'bg-amber-500/20 text-amber-400 border-amber-500/30' },
        applicant: { label: 'JOB APPLICANT', color: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30' },
    };

    const currentRole = userType === 'applicant' ? 'applicant' : (user?.role || 'guest');
    const badge = roleBadges[currentRole] || null;

    const isActive = (path) => location.pathname === path;

    const closeMobileMenu = () => setMobileMenuOpen(false);

    return (
        <nav className="sticky top-0 z-50 glass-card border-b backdrop-blur-xl transition-colors">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex items-center justify-between h-16">
                    
                    {/* Brand / Logo */}
                    <div className="flex items-center space-x-3">
                        <Link to="/" onClick={closeMobileMenu} className="flex items-center space-x-2.5 sm:space-x-3 group">
                            <div className="w-10 h-10 rounded-xl bg-gradient-to-tr from-indigo-600 via-purple-600 to-pink-500 p-0.5 shadow-lg shadow-indigo-500/20 group-hover:scale-105 transition-transform">
                                <div className="w-full h-full bg-slate-950 rounded-[10px] flex items-center justify-center">
                                    <RiBriefcase4Fill className="w-5 h-5 text-indigo-400 group-hover:rotate-12 transition-transform" />
                                </div>
                            </div>
                            <div>
                                <div className="flex items-center space-x-1.5">
                                    <span className="font-extrabold text-lg sm:text-xl tracking-tight bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 bg-clip-text text-transparent">
                                        Avsar <span className="text-[10px] sm:text-xs text-indigo-400 font-normal border border-indigo-500/30 px-1 py-0.2 rounded ml-0.5">अवसर</span>
                                    </span>
                                </div>
                                <span className="text-[9px] sm:text-[10px] text-slate-400 font-medium tracking-wider uppercase block -mt-1">Mega Job Fair 2026</span>
                            </div>
                        </Link>
                    </div>

                    {/* Desktop Nav Links */}
                    <div className="hidden md:flex items-center space-x-1">
                        {userType === 'staff' && (
                            <>
                                {user?.role === 'admin' && (
                                    <Link
                                        to="/admin"
                                        className={`px-3 py-2 rounded-xl text-xs font-bold transition-all flex items-center space-x-1.5 ${
                                            isActive('/admin') ? 'bg-indigo-600/30 text-indigo-400 border border-indigo-500/40' : 'text-slate-400 hover:text-indigo-400 hover:bg-slate-800/40'
                                        }`}
                                    >
                                        <RiShieldUserLine className="w-4 h-4 text-indigo-400" />
                                        <span>Dashboard</span>
                                    </Link>
                                )}

                                {user?.role === 'admin' && (
                                    <Link
                                        to="/users"
                                        className={`px-3 py-2 rounded-xl text-xs font-bold transition-all flex items-center space-x-1.5 ${
                                            isActive('/users') ? 'bg-indigo-600/30 text-indigo-400 border border-indigo-500/40' : 'text-slate-400 hover:text-indigo-400 hover:bg-slate-800/40'
                                        }`}
                                    >
                                        <RiTeamLine className="w-4 h-4 text-cyan-400" />
                                        <span>Users</span>
                                    </Link>
                                )}

                                {(user?.role === 'admin' || user?.role === 'coordinator') && (
                                    <Link
                                        to="/companies"
                                        className={`px-3 py-2 rounded-xl text-xs font-bold transition-all flex items-center space-x-1.5 ${
                                            isActive('/companies') ? 'bg-indigo-600/30 text-indigo-400 border border-indigo-500/40' : 'text-slate-400 hover:text-indigo-400 hover:bg-slate-800/40'
                                        }`}
                                    >
                                        <RiBuilding4Line className="w-4 h-4 text-purple-400" />
                                        <span>Companies</span>
                                    </Link>
                                )}

                                {(user?.role === 'admin' || user?.role === 'coordinator' || user?.role === 'company_hr') && (
                                    <Link
                                        to="/applicants"
                                        className={`px-3 py-2 rounded-xl text-xs font-bold transition-all flex items-center space-x-1.5 ${
                                            isActive('/applicants') ? 'bg-indigo-600/30 text-indigo-400 border border-indigo-500/40' : 'text-slate-400 hover:text-indigo-400 hover:bg-slate-800/40'
                                        }`}
                                    >
                                        <RiUserFollowLine className="w-4 h-4 text-emerald-400" />
                                        <span>Applicants</span>
                                    </Link>
                                )}

                                {user?.role === 'company_hr' && (
                                    <Link
                                        to="/hr-stall"
                                        className={`px-3 py-2 rounded-xl text-xs font-bold transition-all flex items-center space-x-1.5 ${
                                            isActive('/hr-stall') ? 'bg-indigo-600/30 text-indigo-400 border border-indigo-500/40' : 'text-slate-400 hover:text-indigo-400 hover:bg-slate-800/40'
                                        }`}
                                    >
                                        <RiMagicLine className="w-4 h-4 text-amber-400" />
                                        <span>Stall Interview Portal</span>
                                    </Link>
                                )}
                            </>
                        )}

                        {userType === 'applicant' && (
                            <Link
                                to="/applicant-portal"
                                className={`px-3 py-2 rounded-xl text-xs font-bold transition-all flex items-center space-x-1.5 ${
                                    isActive('/applicant-portal') ? 'bg-indigo-600/30 text-indigo-400 border border-indigo-500/40' : 'text-slate-400 hover:text-indigo-400 hover:bg-slate-800/40'
                                }`}
                            >
                                <RiFileTextLine className="w-4 h-4 text-emerald-400" />
                                <span>My Job Fair Slip & Status</span>
                            </Link>
                        )}

                        {/* Public links */}
                        <Link
                            to="/public-register"
                            className={`px-3 py-2 rounded-xl text-xs font-bold transition-all flex items-center space-x-1.5 ${
                                isActive('/public-register') ? 'bg-indigo-600/30 text-indigo-400 border border-indigo-500/40' : 'text-slate-400 hover:text-indigo-400 hover:bg-slate-800/40'
                            }`}
                        >
                            <RiUserAddLine className="w-4 h-4 text-pink-400" />
                            <span>Register Candidate</span>
                        </Link>

                        <Link
                            to="/big-screen"
                            target="_blank"
                            className="px-3 py-1.5 rounded-xl text-xs font-bold bg-gradient-to-r from-rose-500/20 to-indigo-500/20 border border-rose-500/30 text-rose-400 hover:brightness-125 transition-all flex items-center space-x-1.5"
                        >
                            <RiTv2Line className="w-4 h-4 text-rose-400 animate-pulse" />
                            <span>Big Screen Live</span>
                        </Link>
                    </div>

                    {/* Right Controls: Light/Dark Theme Switcher + User Controls */}
                    <div className="flex items-center space-x-2 sm:space-x-3">
                        
                        {/* Light / Dark Mode Toggle Switch Button */}
                        <button
                            onClick={toggleTheme}
                            className="p-2 rounded-xl bg-slate-800/80 hover:bg-slate-700/80 text-amber-400 transition-all border border-slate-700/60 shadow-sm flex items-center justify-center"
                            title={theme === 'dark' ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
                        >
                            {theme === 'dark' ? (
                                <RiSunFill className="w-4 h-4 text-amber-400 hover:rotate-45 transition-transform" />
                            ) : (
                                <RiMoonClearFill className="w-4 h-4 text-indigo-600 hover:-rotate-12 transition-transform" />
                            )}
                        </button>

                        {user ? (
                            <div className="flex items-center space-x-2 sm:space-x-3">
                                {badge && (
                                    <span className={`text-[10px] font-extrabold px-2.5 py-1 rounded-full border ${badge.color}`}>
                                        {badge.label}
                                    </span>
                                )}

                                <div className="text-right hidden lg:block">
                                    <div className="text-xs font-bold leading-tight">{user.name}</div>
                                    <div className="text-[10px] text-slate-400 truncate max-w-[120px]">{user.email || user.mobile_number || user.phone_number}</div>
                                </div>

                                <Link
                                    to="/profile"
                                    onClick={closeMobileMenu}
                                    title="Profile & Password Change"
                                    className="p-2 rounded-xl bg-slate-800/80 hover:bg-slate-700 text-slate-300 transition-colors border border-slate-700/60"
                                >
                                    <RiKey2Line className="w-4 h-4 text-amber-400" />
                                </Link>

                                <button
                                    onClick={handleLogout}
                                    title="Logout"
                                    className="p-2 rounded-xl bg-red-500/10 hover:bg-red-500/20 text-red-400 transition-colors border border-red-500/20"
                                >
                                    <RiLogoutBoxRLine className="w-4 h-4" />
                                </button>
                            </div>
                        ) : (
                            <div className="flex items-center space-x-2">
                                <Link
                                    to="/login"
                                    className="px-4 py-2 rounded-xl text-xs font-black bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-500 hover:to-purple-500 text-white shadow-lg shadow-indigo-600/30 transition-all"
                                >
                                    <span>Login Portal</span>
                                </Link>
                            </div>
                        )}

                        {/* Hamburger Button for Mobile */}
                        <button
                            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                            className="md:hidden p-2 rounded-xl bg-slate-800 text-slate-300 hover:text-white border border-slate-700"
                        >
                            {mobileMenuOpen ? <RiCloseLine className="w-5 h-5" /> : <RiMenu4Line className="w-5 h-5" />}
                        </button>
                    </div>
                </div>
            </div>

            {/* Mobile Drawer Menu */}
            {mobileMenuOpen && (
                <div className="md:hidden glass-card border-b border-slate-800 px-4 pt-2 pb-4 space-y-2 animate-fade-in">
                    {userType === 'staff' && (
                        <>
                            {user?.role === 'admin' && (
                                <Link
                                    to="/admin"
                                    onClick={closeMobileMenu}
                                    className="flex items-center space-x-2 px-3 py-2 rounded-xl text-xs font-bold text-slate-300 hover:bg-slate-800"
                                >
                                    <RiShieldUserLine className="w-4 h-4 text-indigo-400" />
                                    <span>Admin Dashboard</span>
                                </Link>
                            )}

                            {user?.role === 'admin' && (
                                <Link
                                    to="/users"
                                    onClick={closeMobileMenu}
                                    className="flex items-center space-x-2 px-3 py-2 rounded-xl text-xs font-bold text-slate-300 hover:bg-slate-800"
                                >
                                    <RiTeamLine className="w-4 h-4 text-cyan-400" />
                                    <span>User Management</span>
                                </Link>
                            )}

                            {(user?.role === 'admin' || user?.role === 'coordinator') && (
                                <Link
                                    to="/companies"
                                    onClick={closeMobileMenu}
                                    className="flex items-center space-x-2 px-3 py-2 rounded-xl text-xs font-bold text-slate-300 hover:bg-slate-800"
                                >
                                    <RiBuilding4Line className="w-4 h-4 text-purple-400" />
                                    <span>Company Registry</span>
                                </Link>
                            )}

                            {(user?.role === 'admin' || user?.role === 'coordinator' || user?.role === 'company_hr') && (
                                <Link
                                    to="/applicants"
                                    onClick={closeMobileMenu}
                                    className="flex items-center space-x-2 px-3 py-2 rounded-xl text-xs font-bold text-slate-300 hover:bg-slate-800"
                                >
                                    <RiUserFollowLine className="w-4 h-4 text-emerald-400" />
                                    <span>Applicant Registry</span>
                                </Link>
                            )}

                            {user?.role === 'company_hr' && (
                                <Link
                                    to="/hr-stall"
                                    onClick={closeMobileMenu}
                                    className="flex items-center space-x-2 px-3 py-2 rounded-xl text-xs font-bold text-slate-300 hover:bg-slate-800"
                                >
                                    <RiMagicLine className="w-4 h-4 text-amber-400" />
                                    <span>Stall Interview Portal</span>
                                </Link>
                            )}
                        </>
                    )}

                    {userType === 'applicant' && (
                        <Link
                            to="/applicant-portal"
                            onClick={closeMobileMenu}
                            className="flex items-center space-x-2 px-3 py-2 rounded-xl text-xs font-bold text-slate-300 hover:bg-slate-800"
                        >
                            <RiFileTextLine className="w-4 h-4 text-emerald-400" />
                            <span>My Job Fair Slip & Status</span>
                        </Link>
                    )}

                    <Link
                        to="/public-register"
                        onClick={closeMobileMenu}
                        className="flex items-center space-x-2 px-3 py-2 rounded-xl text-xs font-bold text-slate-300 hover:bg-slate-800"
                    >
                        <RiUserAddLine className="w-4 h-4 text-pink-400" />
                        <span>Register Candidate / Company</span>
                    </Link>

                    <Link
                        to="/big-screen"
                        target="_blank"
                        onClick={closeMobileMenu}
                        className="flex items-center space-x-2 px-3 py-2 rounded-xl text-xs font-bold text-rose-400 bg-rose-500/10 border border-rose-500/20"
                    >
                        <RiTv2Line className="w-4 h-4 text-rose-400" />
                        <span>Launch Big Screen Live</span>
                    </Link>
                </div>
            )}
        </nav>
    );
}
